
import { Component, Prop, Vue } from 'vue-property-decorator';

type TriggerMode = 'click' | 'hover';
type Placement = 'left' | 'right' | 'top' | 'bottom';

let popoverCounter = 0;

@Component
export default class HelpPopover extends Vue {
  @Prop({ type: String, default: '' }) readonly helpText!: string;
  @Prop({ type: String, default: 'right' }) readonly placement!: Placement;
  @Prop({ type: Boolean, default: false }) readonly wide!: boolean;
  @Prop({ type: Boolean, default: true }) readonly showCloseButton!: boolean;
  @Prop({ type: String, default: 'close' }) readonly closeButtonText!: string;
  @Prop({ type: String, default: '' }) readonly group!: string;
  @Prop({ 
    type: String, 
    default: 'click',
    validator: (value: string) => ['click', 'hover'].includes(value)
  }) readonly trigger!: TriggerMode;
  @Prop({ type: Number, default: 200 }) readonly hoverShowDelay!: number;
  @Prop({ type: Number, default: 150 }) readonly hoverHideDelay!: number;

  private isOpen = false;
  private computedPlacement: Placement = 'right';
  private popoverId = `help-popover-${++popoverCounter}`;
  
  private showTimeout: number | null = null;
  private hideTimeout: number | null = null;
  private isOverPopover = false;

  private $refs!: {
    trigger: HTMLElement;
    popoverContainer?: HTMLElement;
  };

  private get triggerMode(): TriggerMode {
    return this.trigger;
  }

  mounted() {
    document.addEventListener('keydown', this.handleEscapeKey);
    this.$root.$on('help-popover:close-group', this.handleGroupClose);
  }

  beforeDestroy() {
    this.clearAllTimeouts();
    document.removeEventListener('keydown', this.handleEscapeKey);
    this.$root.$off('help-popover:close-group', this.handleGroupClose);
  }

  public open(): void {
    if (this.isOpen) return;
    
    if (this.group) {
      this.$root.$emit('help-popover:close-group', { groupId: this.group, excludeId: this.popoverId });
    }

    this.isOpen = true;
    this.calculatePlacement();
    this.$emit('open');
  }

  public close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.isOverPopover = false;
    this.clearAllTimeouts();
    this.$emit('close');
  }

  public toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private handleClick(): void {
    if (this.triggerMode === 'click') {
      this.toggle();
    }
  }

  private handleMouseEnter(): void {
    if (this.triggerMode !== 'hover') return;
    this.clearHideTimeout();
    this.showTimeout = window.setTimeout(() => this.open(), this.hoverShowDelay);
  }

  private handleMouseLeave(): void {
    if (this.triggerMode !== 'hover') return;
    this.clearShowTimeout();
    this.hideTimeout = window.setTimeout(() => {
      if (!this.isOverPopover) this.close();
    }, this.hoverHideDelay);
  }

  private handlePopoverMouseEnter(): void {
    if (this.triggerMode !== 'hover') return;
    this.isOverPopover = true;
    this.clearHideTimeout();
  }

  private handlePopoverMouseLeave(): void {
    if (this.triggerMode !== 'hover') return;
    this.isOverPopover = false;
    this.hideTimeout = window.setTimeout(() => this.close(), this.hoverHideDelay);
  }

  private get containerStyle(): Record<string, string> {
    if (!this.isOpen) return { display: 'none' };
    
    // WIDE MODE: Centered on screen, 80% width
    if (this.wide) {
      return {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        zIndex: '1050',
      };
    }

    // STANDARD MODE: Attached to trigger
    const trigger = this.$refs.trigger;
    if (!trigger) return { display: 'none' };

    const rect = trigger.getBoundingClientRect();
    
    // Enforce at least 20% screen width, allow text to expand it, cap at 50vw
    const styles: Record<string, string> = {
      position: 'fixed',
      zIndex: '1050',
      width: 'fit-content',
      minWidth: '20vw', 
      maxWidth: '50vw', 
    };

    switch (this.computedPlacement) {
      case 'right':
        styles.left = `${rect.right + 10}px`;
        styles.top = `${rect.top + rect.height / 2}px`;
        styles.transform = 'translateY(-50%)';
        break;
      case 'left':
        styles.right = `${window.innerWidth - rect.left + 10}px`;
        styles.top = `${rect.top + rect.height / 2}px`;
        styles.transform = 'translateY(-50%)';
        break;
      case 'top':
        styles.left = `${rect.left + rect.width / 2}px`;
        styles.bottom = `${window.innerHeight - rect.top + 10}px`;
        styles.transform = 'translateX(-50%)';
        break;
      case 'bottom':
        styles.left = `${rect.left + rect.width / 2}px`;
        styles.top = `${rect.bottom + 10}px`;
        styles.transform = 'translateX(-50%)';
        break;
    }

    return styles;
  }

  private get arrowClass(): string {
    const arrowMap: Record<Placement, string> = {
      right: '', left: 'arrow-left', top: 'arrow-top', bottom: 'arrow-bottom',
    };
    return arrowMap[this.computedPlacement] || '';
  }

  // =====================
  // Private Methods
  // =====================

  private calculatePlacement(): void {
    if (this.wide) return; // Skip calculation in wide mode

    this.$nextTick(() => {
      const trigger = this.$refs.trigger;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Use the enforced min-width for space calculations
      const estimatedPopoverWidth = Math.max(window.innerWidth * 0.2, 320); 
      const estimatedHeight = 150;

      const spaceOnRight = viewportWidth - rect.right;
      const spaceOnLeft = rect.left;
      const spaceOnTop = rect.top;
      const spaceOnBottom = viewportHeight - rect.bottom;

      if (this.placement === 'right' && spaceOnRight >= estimatedPopoverWidth + 20) {
        this.computedPlacement = 'right';
      } else if (this.placement === 'left' && spaceOnLeft >= estimatedPopoverWidth + 20) {
        this.computedPlacement = 'left';
      } else if (this.placement === 'top' && spaceOnTop >= estimatedHeight + 20) {
        this.computedPlacement = 'top';
      } else if (this.placement === 'bottom' && spaceOnBottom >= estimatedHeight + 20) {
        this.computedPlacement = 'bottom';
      } else {
        // Auto-detect best position respecting the new larger width
        if (spaceOnRight >= estimatedPopoverWidth + 20) this.computedPlacement = 'right';
        else if (spaceOnLeft >= estimatedPopoverWidth + 20) this.computedPlacement = 'left';
        else if (spaceOnBottom >= estimatedHeight + 20) this.computedPlacement = 'bottom';
        else this.computedPlacement = 'top';
      }
    });
  }

  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) this.close();
  }

  private handleGroupClose(payload: { groupId: string; excludeId: string }): void {
    if (this.group === payload.groupId && this.popoverId !== payload.excludeId) {
      this.close();
    }
  }

  private clearShowTimeout(): void {
    if (this.showTimeout !== null) {
      window.clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout !== null) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private clearAllTimeouts(): void {
    this.clearShowTimeout();
    this.clearHideTimeout();
  }
}
